import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useSetupAgent } from "@/hooks/agent/useSetupAgent";
import { useVoiceAgent } from "@/hooks/voice/useVoiceAgent";
import { ConversationMessage } from "@/types/agent/setup";

interface UseGenieConversationBaseOptions {
  onMessageReceived?: (message: ConversationMessage) => void;
  onConversationEnd?: (conversationId: string | null) => Promise<void>;
  mode: "setup" | "update";
}

export const useGenieConversationBase = ({ onMessageReceived, onConversationEnd, mode }: UseGenieConversationBaseOptions) => {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const { agent: setupAgent } = useSetupAgent();
  const voiceAgent = useVoiceAgent(setupAgent?.elevenlabs_id);

  const handleMessage = useCallback(
    (message: string, isUser: boolean) => {
      const newMessage: ConversationMessage = {
        role: isUser ? "user" : "agent",
        message,
        timestamp: Date.now(),
      };
      //console.log("Message created:", newMessage);

      if (onMessageReceived) {
        onMessageReceived(newMessage);
      }
    },
    [onMessageReceived, mode],
  );

  // Handle voice agent message updates and disconnect events
  useEffect(() => {
    const service = voiceAgent.getService();
    if (!service) return;

    service.onMessage(handleMessage);

    // Register onDisconnect callback to trigger analysis
    voiceAgent.onDisconnect(async (conversationId) => {
      if (onConversationEnd && conversationId) {
        await onConversationEnd(conversationId);
      }
    });

    return () => {
      // Cleanup is handled by the useVoiceAgent hook
    };
  }, [setupAgent?.elevenlabs_id, handleMessage, onConversationEnd]);

  const handleConversationToggle = useCallback(async () => {
    if (voiceAgent.connected) {
      // Get conversation ID before ending conversation
      const currentConversationId = voiceAgent.getConversationId();
      setConversationId(currentConversationId);
      console.log("Ending conversation with ID:", currentConversationId);

      await voiceAgent.endConversation();
      // onConversationEnd will be triggered by voiceAgent.onDisconnect callback

      return currentConversationId;
    } else if (!voiceAgent.isConnecting) {
      try {
        // Ensure message callback is registered before starting conversation
        const service = voiceAgent.getService();
        if (service) {
          service.onMessage(handleMessage);
        }

        const success = await voiceAgent.startConversation();

        if (!success) {
          console.error(`❌ Failed to start ${mode} conversation`);
          toast.error("Failed to start conversation. Please try again.");
          return null;
        }
      } catch (err) {
        console.error(`❌ Error starting ${mode} conversation:`, err);
        toast.error("Error starting conversation. Please try again.");
        return null;
      }
    }
    //console.log(`✅ ${mode} conversation started successfully`);
    return null;
  }, [voiceAgent, handleMessage, onConversationEnd, mode]);

  return {
    // Voice agent state
    speaking: voiceAgent.speaking,
    listening: voiceAgent.listening,
    thinking: voiceAgent.thinking,
    connected: voiceAgent.connected,
    isConnecting: voiceAgent.isConnecting,

    voiceAgent,
    conversationId,
    setupAgent,
    handleConversationToggle,
    resetConversation: useCallback(() => {
      setConversationId(null);
    }, []),
  };
};
